/**
 * useStageOfferEntry — la carte du stage de vacances : ce qu'elle mesure et le geste qui ouvre
 * le parcours d'inscription.
 *
 * Deux événements, aux noms fixes (la surface, la ligne du calendrier et le stage voyagent en
 * propriétés) : `parent_stage_offer.shown` chaque fois que la carte arrive à l'écran pour un
 * enfant, `parent_stage_offer.clicked` au clic. La mesure qui fait foi reste la source d'entrée
 * que le funnel garde côté serveur (`source=parent_home_banner`, portée par le lien).
 *
 * Le NAVIGATEUR du système, comme le lien de reprise : le funnel stage n'existe que sur le web,
 * et il reconnaît la famille par son lien.
 */
import { useCallback, useMemo } from "react"
import { Linking } from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"

import { type UIParentStageOffer } from "../types"

const TRACKING_SOURCE = "parent_stage_offer"
const SURFACE = "parent_home_banner"

function eventProperties(offer: UIParentStageOffer) {
    return { surface: SURFACE, offer_id: offer.offerId, stage: offer.stageSlug, label: offer.title }
}

export function useStageOfferEntry(
    offer: UIParentStageOffer | null,
    studentId: string | number | null | undefined,
    /** La page montre son contenu : ni squelette, ni écran d'erreur. */
    pageReady: boolean
): { onOpenStageOffer?: () => void } {
    const offerId = offer?.offerId ?? null

    // L'accueil reste monté derrière les autres onglets, et les chips enfant du header changent
    // l'élève depuis n'importe lequel : la carte ne se compte que l'accueil devant. Un effet de
    // focus, pas `useIsFocused` : celui-ci ferait re-rendre tout l'accueil à chaque changement
    // d'onglet.
    useFocusEffect(
        useCallback(() => {
            if (!pageReady || offer == null || studentId == null) return
            amplitude.track(`${TRACKING_SOURCE}.shown`, eventProperties(offer))
            // `offer` change d'identité à chaque lecture du view-model, pas l'offre.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [pageReady, offerId, studentId])
    )

    const onOpenStageOffer = useMemo(() => {
        if (offer == null) return undefined
        return () => {
            amplitude.track(`${TRACKING_SOURCE}.clicked`, eventProperties(offer))
            void Linking.openURL(offer.url)
        }
    }, [offer])

    return { onOpenStageOffer }
}
